import { motion } from "framer-motion";

const components = {
  section: motion.section,
  div: motion.div,
  article: motion.article,
  button: motion.button,
  a: motion.a,
  figure: motion.figure,
  aside: motion.aside,
  footer: motion.footer,
  h1: motion.h1,
  h2: motion.h2,
  h3: motion.h3,
  p: motion.p,
  span: motion.span,
  strong: motion.strong,
  small: motion.small,
  ul: motion.ul,
  li: motion.li,
  img: motion.img,
};

const easeOut = [0.22, 1, 0.36, 1];

const revealVariants = {
  rise: {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: easeOut },
    },
  },
  soft: {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.58, ease: easeOut },
    },
  },
  left: {
    hidden: { opacity: 0, x: -36 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.72, ease: easeOut },
    },
  },
  right: {
    hidden: { opacity: 0, x: 36 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.72, ease: easeOut },
    },
  },
  image: {
    hidden: { opacity: 0, scale: 0.94, y: 22 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.82, ease: easeOut },
    },
  },
  card: {
    hidden: { opacity: 0, y: 34, scale: 0.97 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.66, ease: easeOut },
    },
  },
};

export const revealItem = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: easeOut },
  },
};

const wordItem = {
  hidden: { opacity: 0, y: "0.72em", rotateX: -26 },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: { duration: 0.62, ease: easeOut },
  },
};

function getMotionComponent(as) {
  return components[as] || motion.div;
}

export function MotionReveal({
  as = "div",
  delay = 0,
  amount = 0.18,
  variant = "rise",
  variants,
  children,
  ...props
}) {
  const Component = getMotionComponent(as);
  const selectedVariant = variants || revealVariants[variant] || revealVariants.rise;
  const delayedVariant =
    delay && selectedVariant.visible
      ? {
          ...selectedVariant,
          visible: {
            ...selectedVariant.visible,
            transition: {
              ...(selectedVariant.visible.transition || {}),
              delay,
            },
          },
        }
      : selectedVariant;

  return (
    <Component
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      variants={delayedVariant}
      {...props}
    >
      {children}
    </Component>
  );
}

export function MotionGroup({
  as = "div",
  stagger = 0.08,
  delay = 0,
  amount = 0.14,
  children,
  ...props
}) {
  const Component = getMotionComponent(as);

  return (
    <Component
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      variants={{
        hidden: {},
        visible: {
          transition: { staggerChildren: stagger, delayChildren: delay },
        },
      }}
      {...props}
    >
      {children}
    </Component>
  );
}

export function MotionItem({
  as = "div",
  variant = "soft",
  variants,
  children,
  ...props
}) {
  const Component = getMotionComponent(as);
  const selectedVariant =
    variants || revealVariants[variant] || revealItem;

  return (
    <Component variants={selectedVariant} {...props}>
      {children}
    </Component>
  );
}

export function MotionText({
  as = "h2",
  text,
  delay = 0,
  stagger = 0.045,
  amount = 0.55,
  decorative = false,
  className,
  wordClassName = "tgm-motion-word",
  ...props
}) {
  const Component = getMotionComponent(as);
  const words = String(text || "").split(" ");
  const accessibility = decorative
    ? { "aria-hidden": "true" }
    : { "aria-label": text };

  return (
    <Component
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      variants={{
        hidden: {},
        visible: {
          transition: { staggerChildren: stagger, delayChildren: delay },
        },
      }}
      {...accessibility}
      {...props}
    >
      {words.map((word, index) => (
        <motion.span
          aria-hidden="true"
          className={wordClassName}
          variants={wordItem}
          key={`${word}-${index}`}
        >
          {word}
          {index < words.length - 1 ? "\u00a0" : ""}
        </motion.span>
      ))}
    </Component>
  );
}
